import { Box, Stack } from '@mui/material';
import { colors, IconButton, Typography } from '@sweep-io/sweep-design';
import { Close } from '@sweep-io/sweep-design/dist/icons';
import { useState, useCallback, useEffect } from 'react';
import { TextFieldWithButtonV2 } from '../../../../common/ai-chat/input-atoms/TextFieldWithButtonV2';
import { AiChatUserMessageBox } from '../../../../common/ai-chat/message-atoms/AiChatUserMessageBox';
import { AiChatAssistantMessageBoxSimple } from '../../../../common/ai-chat/message-atoms/AiChatAssistantMessageBoxSimple';
import { useMetadataMapping } from '../useMetadataMapping';
import { usePageHelpers } from '../usePageHelpers';

interface MetadataMappingPanelProps {
  onClose: () => void;
}

export const MetadataMappingChatPanel = ({ onClose }: MetadataMappingPanelProps) => {
  const { funnelMapId } = usePageHelpers();
  const { status, error, message, isLoading, sendMessage, complete } = useMetadataMapping(funnelMapId || '');
  const [inputValue, setInputValue] = useState('');
  const [sentMessage, setSentMessage] = useState<string | null>(null);

  useEffect(() => {
    if (status === 'completedFetchingData') {
      (async () => {
        await complete();
        setSentMessage(null);
        onClose();
      })();
    }
  }, [status, complete, onClose]);

  const handleSendMessage = useCallback(
    async (messageText: string) => {
      setSentMessage(messageText);
      await sendMessage(messageText);
    },
    [sendMessage],
  );

  const canSendMessage = status === 'idle' || status === 'waitingForInput' || status === 'error';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <Box
        sx={{
          height: '56px',
          borderBottom: `1px solid ${colors.grey[120]}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
        }}
      >
        <Typography variant="h5" color={colors.night[900]}>
          Metadata Mapping Agent
        </Typography>
        <IconButton variant="flat" onClick={onClose} size="tiny">
          <Close />
        </IconButton>
      </Box>

      <Stack
        sx={{
          flex: 1,
          padding: '24px',
          paddingLeft: '12px', //additional 12px is coming from AiChatAssistantMessageBoxSimple
          gap: '16px',
          overflow: 'auto',
        }}
      >
        <AiChatAssistantMessageBoxSimple content="Would you like to add or remove anything?"/>


        <Stack
          sx={{
            flex: 1,
            gap: '16px',
            justifyContent: sentMessage || message || error ? 'flex-start' : 'flex-end',
          }}
        >
          {sentMessage && (
            <AiChatUserMessageBox content={sentMessage} />
          )}
          {status === 'waitingForInput' && message && (
            <AiChatAssistantMessageBoxSimple content={message} />
          )}
          {status === 'error' && error && (
            <AiChatAssistantMessageBoxSimple content={error} errorState />
          )}
        </Stack>
      </Stack>

      <Box
        sx={{
          padding: '16px',
        }}
      >
        <TextFieldWithButtonV2
          value={inputValue}
          setValue={setInputValue}
          onConfirm={handleSendMessage}
          inputPlaceholder="Ask for changes"
          isLoading={isLoading}
          readonly={!canSendMessage}
        />
      </Box>
    </Box>
  );
};

